import {
  Create as CreateIcon,
  AttachMoney as DollarIcon,
  ControlPointDuplicate as ExtraCreditsIcon,
  OpenInNew as OpenLinkIcon,
  Payments as PaymentsIcon,
  PeopleAlt as PeopleIcon,
  FilterNone as PositionsIcon,
  Schedule as ScheduleIcon,
  Toll as TollIcon,
} from '@mui/icons-material';
import {
  Button,
  Card,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select/SelectInput';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { isNil, startCase } from 'lodash-es';
import log from 'loglevel';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UserRoleName } from '../../@types/user';
import { BillingPeriod, WorkflowType } from '../../api/paymentsApi';
import { AccountPlanBillingCycle, PricingPlan } from '../../consts/enums';
import { useGetAccountPlanDefinitions } from '../../hooks/query/useAccounts';
import { useCreateAccountPlan } from '../../hooks/useAccount';
import useInviteUserToAccount from '../../hooks/useInviteUserToAccount';
import { useCreatePaymentLinkMutation } from '../../hooks/usePayments';
import { addDays } from '../AccountsPage/formik';
import { PLAN_NAMES } from './ChoosePlan.consts';
import { getChoosePlanFormSchema } from './ChoosePlan.schema';
import { parseAsNumber } from './ChoosePlan.utils';

type ChoosePlanFormState = {
  pricingPlan: PricingPlan;
  maximumOpenPositions: number;
  maxUserSeats: number;
  monthlyCredits: number;
  extraMonthlyCredits: number;
  customUnitPrice: number;
  endDate: string;
  billingPeriod: BillingPeriod;
};

export const ChoosePlanPage = (): JSX.Element => {
  const [searchParams] = useSearchParams();

  const isNewAccount = searchParams.get('isNewAccount') === 'true';
  const accountId = searchParams.get('accountId') ?? null;
  const userEmail = decodeURIComponent(searchParams.get('userEmail'));

  const createAccountPlan = useCreateAccountPlan();
  const createPaymentLink = useCreatePaymentLinkMutation();
  const { mutateAsync: inviteUserToAccount } = useInviteUserToAccount();

  const { data: planDefinitions = [] } = useGetAccountPlanDefinitions();

  const planOptions = planDefinitions
    .filter(({ pricingPlan }) => [PricingPlan.Trial, PricingPlan.Tier1B, PricingPlan.Scale].includes(
        pricingPlan,
      ),
    )
    .map(({ pricingPlan }) => ({
      displayName: PLAN_NAMES[pricingPlan],
      pricingPlan,
    }));

  const [formError, setFormError] = useState<string>(null);

  const formik = useFormik<ChoosePlanFormState>({
    initialValues: {
      pricingPlan: null,
      maximumOpenPositions: null,
      maxUserSeats: 1,
      monthlyCredits: null,
      extraMonthlyCredits: null,
      customUnitPrice: null,
      endDate: addDays(new Date(), 7).toISOString(),
      billingPeriod: BillingPeriod.Monthly,
    },
    validateOnChange: false,
    validationSchema: getChoosePlanFormSchema(planDefinitions),
    onSubmit: async (values): Promise<void> => {
      const {
        pricingPlan,
        maximumOpenPositions,
        maxUserSeats,
        monthlyCredits,
        extraMonthlyCredits,
        customUnitPrice,
        billingPeriod,
        endDate,
      } = values;
      const planDefinition = planDefinitions.find(
        (definition) => pricingPlan === definition.pricingPlan,
      );

      try {
        if (planDefinition?.isPaidPlan) {
          const { url: paymentLink } = await createPaymentLink.mutateAsync({
            accountId,
            planDefinitionId: planDefinition?.id,
            billingPeriod,
            quantity: planDefinition?.isCreditsBased ?
              parseAsNumber(maxUserSeats) :
              parseAsNumber(maximumOpenPositions),
            customUnitPrice: parseAsNumber(customUnitPrice),
            allowAdjustableQuantity: false,
            workflowType: WorkflowType.BackOfficeSales,
            workflowData: {
              monthlyCreditsToSet: planDefinition?.isCreditsBased ?
                parseAsNumber(monthlyCredits) +
                  parseAsNumber(extraMonthlyCredits ?? 0) :
                undefined,
              userEmailToInvite: userEmail,
            },
          });

          window.open(paymentLink, '_blank');
        } else {
          const promises: Promise<unknown>[] = [
            createAccountPlan.mutateAsync({
              accountId,
              plan: {
                accountPlanDefinitionId: planDefinition.id,
                billingCycle:
                  pricingPlan === PricingPlan.Trial ?
                    undefined :
                    AccountPlanBillingCycle.MONTHLY,
                isCreditsBased: planDefinition?.isCreditsBased,
                maximumOpenPositions: planDefinition?.isCreditsBased ?
                  undefined :
                  parseAsNumber(maximumOpenPositions),
                maxUserSeats: planDefinition?.isCreditsBased ?
                  parseAsNumber(maxUserSeats) :
                  undefined,
                monthlyCredits: planDefinition?.isCreditsBased ?
                  parseAsNumber(monthlyCredits) +
                    parseAsNumber(extraMonthlyCredits ?? 0) :
                  undefined,
                endDate,
              },
            }),
          ];

          if (isNewAccount) {
            promises.push(
              inviteUserToAccount({
                accountId,
                email: userEmail,
                userRoleName: UserRoleName.Admin,
              }),
            );
          }

          await Promise.all(promises);
        }
      } catch (e) {
        const err = e as AxiosError;
        const message = err.response?.data?.message;
        log.error(err);
        setFormError(message);
      }
    },
  });

  const {
    handleSubmit,
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    isValid,
    isSubmitting,
  } = formik;

  const currentPlanDefinition = planDefinitions.find(
    ({ pricingPlan }) => pricingPlan === values.pricingPlan,
  );
  const isMonthlyCreditsAmountFixed = !isNil(
    currentPlanDefinition?.monthlyCredits,
  );

  const handlePricingPlanChange = async (event: SelectChangeEvent) => {
    handleChange(event);
    const newPlan = event.target.value as PricingPlan;
    const newPlanDefinition = planDefinitions.find(
      ({ pricingPlan }) => newPlan === pricingPlan,
    );

    await setFieldValue(
      'monthlyCredits',
      newPlanDefinition?.monthlyCredits,
      true,
    );
    await setFieldValue('extraMonthlyCredits', 0, true);
    await setFieldValue(
      'maxUserSeats',
      newPlanDefinition?.defaultMaxUserSeats,
      true,
    );
    await setFieldValue('customUnitPrice', null, true);
    await setFieldValue(
      'endDate',
      newPlan === PricingPlan.Trial ? addDays(new Date(), 7) : null,
      true,
    );
  };

  const getFormProps = (fieldName: keyof ChoosePlanFormState) => ({
    name: fieldName,
    error: touched[fieldName] && !!errors[fieldName],
    helperText: touched[fieldName] && errors[fieldName],
    value: values[fieldName],
    onChange: handleChange,
    onBlur: handleBlur,
  });

  useEffect(() => {
    if (!accountId) {
      setFormError('`accountId` was not set in the URL.');
    }

    return () => {
      if (!accountId) {
        setFormError(null);
      }
    };
  }, [accountId]);

  return (
    <Container maxWidth="md" sx={ { paddingBlock: '20px' } }>
      <Stack
        direction="row"
        alignItems="center"
        spacing={ 1 }
        sx={ { paddingBlock: '24px' } }
      >
        <PaymentsIcon fontSize="large" />

        <Typography variant="h3">Choose Plan</Typography>
      </Stack>

      <Card sx={ { padding: '24px' } }>
        <form onSubmit={ handleSubmit }>
          <Stack spacing={ 3 }>
            <Grid container spacing={ 2 }>
              <Grid item xs={ 3 }>
                <Stack>
                  <FormControl fullWidth>
                    <InputLabel>Pricing Plan</InputLabel>
                    <Select
                      label="Pricing Plan"
                      { ...getFormProps('pricingPlan') }
                      onChange={ handlePricingPlanChange }
                      disabled={ isSubmitting }
                      value={ values.pricingPlan ?? '' }
                    >
                      {planOptions.map(({ displayName, pricingPlan }) => (
                        <MenuItem key={ pricingPlan } value={ pricingPlan }>
                          {displayName}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Trial option is only available for newly-created accounts.
                    </FormHelperText>
                  </FormControl>
                </Stack>
              </Grid>

              {currentPlanDefinition?.isPaidPlan && (
                <Grid item xs={ 3 }>
                  <FormControl fullWidth>
                    <InputLabel>Billing Period</InputLabel>
                    <Select
                      label="Billing Period"
                      { ...getFormProps('billingPeriod') }
                      disabled={ !values.pricingPlan || isSubmitting }
                      value={ values.billingPeriod ?? '' }
                      startAdornment={ (
                        <InputAdornment position="start">
                          <ScheduleIcon />
                        </InputAdornment>
                      ) }
                    >
                      {[
                        BillingPeriod.Monthly,
                        BillingPeriod.Yearly,
                        BillingPeriod.Quarterly,
                        BillingPeriod.SemiYearly,
                      ].map((billingPeriod) => (
                        <MenuItem key={ billingPeriod } value={ billingPeriod }>
                          {startCase(billingPeriod)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {currentPlanDefinition?.isCreditsBased && (
                <Grid item xs={ 3 }>
                  <TextField
                    label="User Seats"
                    { ...getFormProps('maxUserSeats') }
                    fullWidth
                    InputLabelProps={ { shrink: true } }
                    disabled={ !values.pricingPlan || isSubmitting }
                    InputProps={ {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PeopleIcon />
                        </InputAdornment>
                      ),
                    } }
                  />
                </Grid>
              )}

              {currentPlanDefinition && !currentPlanDefinition?.isCreditsBased && (
                <Grid item xs={ 3 }>
                  <TextField
                    label="Maximum Open Positions"
                    { ...getFormProps('maximumOpenPositions') }
                    fullWidth
                    InputLabelProps={ { shrink: true } }
                    disabled={ !values.pricingPlan || isSubmitting }
                    InputProps={ {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PositionsIcon />
                        </InputAdornment>
                      ),
                    } }
                  />
                </Grid>
              )}

              {currentPlanDefinition?.isCreditsBased && (
                <Grid item xs={ 3 }>
                  <TextField
                    label="Monthly Credits Per User"
                    { ...getFormProps('monthlyCredits') }
                    value={ values.monthlyCredits ?? '' }
                    fullWidth
                    InputLabelProps={ { shrink: true } }
                    disabled={
                      isMonthlyCreditsAmountFixed ||
                      !values.pricingPlan ||
                      isSubmitting
                    }
                    InputProps={ {
                      startAdornment: (
                        <InputAdornment position="start">
                          <TollIcon />
                        </InputAdornment>
                      ),
                    } }
                  />
                </Grid>
              )}

              {currentPlanDefinition?.isCreditsBased &&
                isMonthlyCreditsAmountFixed && (
                  <Grid item xs={ 3 }>
                    <TextField
                      label="Extra Monthly Credits"
                      { ...getFormProps('extraMonthlyCredits') }
                      value={ values.extraMonthlyCredits ?? '' }
                      fullWidth
                      InputLabelProps={ { shrink: true } }
                      disabled={ !values.pricingPlan || isSubmitting }
                      InputProps={ {
                        startAdornment: (
                          <InputAdornment position="start">
                            <ExtraCreditsIcon />
                          </InputAdornment>
                        ),
                      } }
                    />
                  </Grid>
                )}

              {values.pricingPlan === PricingPlan.Trial && (
                <Grid item xs={ 3 }>
                  <LocalizationProvider dateAdapter={ AdapterDateFns }>
                    <DatePicker
                      inputFormat="dd/MM/yyyy"
                      label="Trial End Date"
                      { ...getFormProps('endDate') }
                      onChange={ (newValue) => setFieldValue(
                          'endDate',
                          newValue?.toISOString() ?? null,
                        ) }
                      value={ values.endDate ?? '' }
                      disabled={ !values.pricingPlan || isSubmitting }
                      renderInput={ (params) => <TextField { ...params } /> }
                      minDate={ addDays(new Date(), 1) }
                    />
                  </LocalizationProvider>
                </Grid>
              )}

              {currentPlanDefinition?.isCustomPlan && (
                <Grid item xs={ 3 }>
                  <TextField
                    label={ `Price per ${
                      currentPlanDefinition?.isCreditsBased ?
                        'seat' :
                        'position'
                    }` }
                    { ...getFormProps('customUnitPrice') }
                    value={ values.customUnitPrice ?? '' }
                    fullWidth
                    InputLabelProps={ { shrink: true } }
                    disabled={ !values.pricingPlan || isSubmitting }
                    InputProps={ {
                      startAdornment: (
                        <InputAdornment position="start">
                          <DollarIcon />
                        </InputAdornment>
                      ),
                    } }
                  />
                </Grid>
              )}
            </Grid>

            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              spacing={ 2 }
            >
              {formError && <Typography color="error">{formError}</Typography>}

              <Stack spacing={ 1 } alignItems="flex-end">
                {createAccountPlan.isSuccess ? (
                  <Typography color="success">Plan created!</Typography>
                ) : (
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={ !isValid || isSubmitting || !accountId }
                    startIcon={
                      currentPlanDefinition?.isPaidPlan ? (
                        <OpenLinkIcon />
                      ) : (
                        <CreateIcon />
                      )
                    }
                  >
                    {currentPlanDefinition?.isPaidPlan ?
                      'Go to payment link' :
                      'Create plan'}
                  </Button>
                )}

                {currentPlanDefinition?.isPaidPlan && (
                  <Typography
                    variant="caption"
                    textAlign="end"
                    sx={ { maxWidth: '210px' } }
                  >
                    Once the payment is done, the user will be invited to the
                    account as an Admin, and his plan will be updated with the
                    set monthly credits.
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
        </form>
      </Card>
    </Container>
  );
};
